import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import apiConfigurations from 'api/configurations';
import apiProjects from 'api/projects';
import getCsvData from 'components/configurator/services/getCsvData';
import getSaveData from 'services/configurator/getSaveData';
import IRouteParams from 'interfaces/IRouteParams';
import IWallMetaData from 'components/interfaces/IWallMetaData';
import SidebarSaveIcon from 'components/layout/buttons/SidebarSaveIcon';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { FLOAT } from 'services/constants/glassPropertyTypes';
import { KRH_VERTICAL_SIDES } from 'services/constants/glassEdgeTreatmentTypes';
import { LAMINATED } from 'services/constants/glassTreatmentTypes';
import { setConfiguration } from 'store/configuration/actions';
import { configurationSaved } from 'store/configurationChange/actions';

import {
  MAX_WEIGHT,
  QUANTITY,
} from 'services/constants/glassWidthCalculationTypes';

import SaveConfigurationModal from '../../layout/navbar/SaveConfigurationModal';

import 'style/layout/Buttons.css';

const SaveConfiguration = ({
  configName,
}: {
  configName: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const configuration = useSelector((state: any) => state.configuration);
  const wallsMetaData = useSelector((state: any) => state.wallsMetaData);
  const { t } = useTranslation([translations.common]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { projectId, configurationId } = useParams<IRouteParams>();
  const history = useHistory();

  const settings = useSelector(
    (state: any) => state.configurationViewer.settings
  );

  const wallMetaDataWithPreSaveActions = () =>
    wallsMetaData.map((wallMetaData: IWallMetaData) => ({
      ...wallMetaData,
      glassAreas: wallMetaData.glassAreas.map((glassArea, index) => ({
        ...glassArea,
        edgeTreatmentType:
          // This mapping can be removed when we can be pretty sure all treatment types have been set (24.5.2021)
          glassArea.edgeTreatmentType === undefined ||
          glassArea.edgeTreatmentType === null
            ? KRH_VERTICAL_SIDES
            : glassArea.edgeTreatmentType,
        glassPropertyType:
          // This mapping can be removed when we can be pretty sure all property types have been set (24.5.2021)
          glassArea.glassPropertyType === undefined ||
          glassArea.glassPropertyType === null
            ? FLOAT
            : glassArea.glassPropertyType,
        glassTreatmentType:
          // This mapping can be removed when we can be pretty sure all treatment types have been set (24.5.2021)
          glassArea.glassTreatmentType === undefined ||
          glassArea.glassTreatmentType === null
            ? LAMINATED
            : glassArea.glassTreatmentType,
        index: index + 1,
        maxWeight:
          glassArea.calculationType === MAX_WEIGHT ? glassArea.maxWeight : null,
        notice:
          // This mapping can be removed when we can be pretty sure all notices have values (24.5.2021)
          glassArea.notice === undefined || glassArea.notice === null
            ? ''
            : glassArea.notice,
        quantity:
          glassArea.calculationType === QUANTITY ? glassArea.quantity : null,
      })),
    }));

  const save = (configurationName: string, newProjectId: string) => {
    const data = getSaveData(settings.scene);
    const csvData = getCsvData();
    console.log('Recipe:', data);

    const updateConfigurationNameInStore = () => {
      if (newProjectId !== '') {
        const getProject = async () => {
          const project = await apiProjects.get(newProjectId);
          dispatch(
            setConfiguration({
              ...configuration,
              name: configurationName,
              project,
            })
          );
        };
        getProject();
        return;
      }
      dispatch(setConfiguration({ ...configuration, name: configurationName }));
    };

    if (configurationName === '') {
      dispatch(
        addMessage(t(`${translations.common}:Modal Messages.Fill All Fields`))
      );

      return;
    }

    if (newProjectId !== '' || projectId !== undefined) {
      const postConfig = async () => {
        try {
          const result = await apiConfigurations.post(
            {
              name: configurationName,
              data,
              csvData,
              walls: wallMetaDataWithPreSaveActions(),
            },
            projectId !== undefined ? projectId : newProjectId
          );
          setShowSaveModal(false);
          updateConfigurationNameInStore();
          dispatch(configurationSaved());
          history.push(`/configurations/${result.id}`);
        } catch (error) {
          dispatch(
            addMessage(t(`${translations}:Configuration saving failed`))
          );
        }
      };
      postConfig().catch((e) => console.log(e));
      return;
    }

    if (configurationId !== undefined) {
      const putConfig = async () => {
        try {
          const result = await apiConfigurations.put(
            {
              name: configurationName,
              data,
              csvData,
              walls: wallMetaDataWithPreSaveActions(),
            },
            configurationId
          );
          setShowSaveModal(false);
          dispatch(configurationSaved());
          history.replace(`/configurations/${result.id}`);
          updateConfigurationNameInStore();
        } catch (error) {
          dispatch(
            addMessage(t(`${translations}:Configuration saving failed`))
          );
        }
      };
      putConfig().catch((e) => console.log(e));
    }
  };

  return (
    <>
      <SidebarSaveIcon
        className="left-bar-icon"
        onClick={() => setShowSaveModal(true)}
      />
      <SaveConfigurationModal
        showSave={showSaveModal}
        setShowSave={setShowSaveModal}
        saveName={save}
        placeholder={configName}
      />
    </>
  );
};

export default SaveConfiguration;
